import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material'
import {
  Visibility,
  FilterList,
  Download,
  Refresh,
  Assignment
} from '@mui/icons-material'
import { format } from 'date-fns'
import axios from 'axios'
import { API_BASE_URL } from '../config/api'

interface RequirementItem {
  product_name: string
  variety_breed?: string
  quantity: number
  unit: string
  unit_price_min?: number
  unit_price_max?: number
}

interface BuyerRequirement {
  id: string
  buyer_id: string
  buyer_name?: string
  buyer_email?: string
  title: string
  description?: string
  category: string
  status: string
  total_budget: number
  currency: string
  delivery_location?: any
  delivery_date_from: string
  delivery_date_to: string
  payment_terms?: string
  quality_requirements?: string
  certification_required: boolean
  organic_required: boolean
  created_at: string
  expires_at: string
  items?: RequirementItem[]
}

const AllBuyerRequirements: React.FC = () => {
  const [requirements, setRequirements] = useState<BuyerRequirement[]>([])
  const [filteredRequirements, setFilteredRequirements] = useState<BuyerRequirement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedRequirement, setSelectedRequirement] = useState<BuyerRequirement | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [minBudget, setMinBudget] = useState('')
  const [maxBudget, setMaxBudget] = useState('')

  // Statistics
  const [stats, setStats] = useState({
    totalRequirements: 0,
    activeRequirements: 0,
    totalBudget: 0,
    fulfilledRequirements: 0,
    expiredRequirements: 0,
    cancelledRequirements: 0
  })

  useEffect(() => {
    fetchRequirements()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [requirements, searchTerm, categoryFilter, statusFilter, startDate, endDate, minBudget, maxBudget])

  const fetchRequirements = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('token')
      
      const response = await axios.get(`${API_BASE_URL}/purchase-requirements/admin/all`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data.success) {
        setRequirements(response.data.data)
        calculateStats(response.data.data)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch buyer requirements')
      console.error('Error fetching buyer requirements:', err)
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (requirementsData: BuyerRequirement[]) => {
    const totalRequirements = requirementsData.length
    const totalBudget = requirementsData.reduce((sum, req) => sum + (Number(req.total_budget) || 0), 0)
    const activeRequirements = requirementsData.filter(req => req.status === 'active').length
    const fulfilledRequirements = requirementsData.filter(req => req.status === 'fulfilled').length
    const expiredRequirements = requirementsData.filter(req => req.status === 'expired').length
    const cancelledRequirements = requirementsData.filter(req => req.status === 'cancelled').length

    setStats({
      totalRequirements,
      activeRequirements,
      totalBudget,
      fulfilledRequirements,
      expiredRequirements,
      cancelledRequirements
    })
  }

  const applyFilters = () => {
    let filtered = [...requirements]

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(req =>
        req.title?.toLowerCase().includes(search) ||
        req.buyer_name?.toLowerCase().includes(search) ||
        req.buyer_email?.toLowerCase().includes(search) ||
        req.category?.toLowerCase().includes(search) ||
        req.description?.toLowerCase().includes(search) ||
        req.id.toLowerCase().includes(search)
      )
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(req => req.category === categoryFilter)
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter)
    }

    // Date range filter (delivery date)
    if (startDate || endDate) {
      filtered = filtered.filter(req => {
        const deliveryFrom = new Date(req.delivery_date_from)
        const deliveryTo = new Date(req.delivery_date_to)
        const start = startDate ? new Date(startDate) : null
        const end = endDate ? new Date(endDate) : null
        
        if (start && end) {
          return (deliveryFrom >= start && deliveryFrom <= end) || (deliveryTo >= start && deliveryTo <= end)
        } else if (start) {
          return deliveryFrom >= start || deliveryTo >= start
        } else if (end) {
          return deliveryFrom <= end || deliveryTo <= end
        }
        return true
      })
    }

    // Budget filter
    if (minBudget) {
      filtered = filtered.filter(req => Number(req.total_budget) >= parseFloat(minBudget))
    }
    if (maxBudget) {
      filtered = filtered.filter(req => Number(req.total_budget) <= parseFloat(maxBudget))
    }

    setFilteredRequirements(filtered)
    setPage(0)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleViewDetails = (requirement: BuyerRequirement) => {
    setSelectedRequirement(requirement)
    setDetailsOpen(true)
  }

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'success' | 'info' | 'warning' | 'error' | 'default' } = {
      active: 'success',
      fulfilled: 'info',
      expired: 'warning',
      cancelled: 'error'
    }
    return colors[status] || 'default'
  }

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      crops: 'Crops',
      fruits: 'Fruits',
      livestock: 'Livestock',
      processed: 'Processed'
    }
    return labels[category] || category
  }

  const exportToCSV = () => {
    const headers = ['ID', 'Buyer', 'Title', 'Category', 'Status', 'Budget', 'Delivery From', 'Delivery To', 'Created', 'Expires']
    const rows = filteredRequirements.map(req => [
      req.id,
      req.buyer_name || req.buyer_email || 'Unknown',
      req.title,
      getCategoryLabel(req.category),
      req.status,
      `${req.currency} ${Number(req.total_budget || 0).toFixed(2)}`,
      format(new Date(req.delivery_date_from), 'yyyy-MM-dd'),
      format(new Date(req.delivery_date_to), 'yyyy-MM-dd'),
      format(new Date(req.created_at), 'yyyy-MM-dd'),
      format(new Date(req.expires_at), 'yyyy-MM-dd')
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `buyer-requirements-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Buyer Requirements Management
        </Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRequirements} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton onClick={exportToCSV} color="primary">
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Requirements
              </Typography>
              <Typography variant="h5">{stats.totalRequirements}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Budget
              </Typography>
              <Typography variant="h5">${Number(stats.totalBudget || 0).toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Active
              </Typography>
              <Typography variant="h5" color="success.main">{stats.activeRequirements}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Fulfilled
              </Typography>
              <Typography variant="h5" color="info.main">{stats.fulfilledRequirements}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Expired
              </Typography>
              <Typography variant="h5" color="warning.main">{stats.expiredRequirements}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Cancelled
              </Typography>
              <Typography variant="h5" color="error.main">{stats.cancelledRequirements}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterList />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Grid container spacing={2}>
          {/* First Row */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Buyer, title, category, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="crops">Crops</MenuItem>
                <MenuItem value="fruits">Fruits</MenuItem>
                <MenuItem value="livestock">Livestock</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="fulfilled">Fulfilled</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Second Row */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Min Budget"
              type="number"
              value={minBudget}
              onChange={(e) => setMinBudget(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Max Budget"
              type="number"
              value={maxBudget}
              onChange={(e) => setMaxBudget(e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Requirements Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Buyer</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Budget</TableCell>
              <TableCell>Delivery Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequirements
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((requirement) => (
                <TableRow key={requirement.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {requirement.buyer_name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {requirement.buyer_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{requirement.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getCategoryLabel(requirement.category)} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {requirement.currency} {Number(requirement.total_budget || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {format(new Date(requirement.delivery_date_from), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" display="block">
                      to {format(new Date(requirement.delivery_date_to), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={requirement.status}
                      color={getStatusColor(requirement.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {format(new Date(requirement.created_at), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {format(new Date(requirement.expires_at), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(requirement)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {filteredRequirements.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary" py={3}>
                    No buyer requirements found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRequirements.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Requirement Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Assignment />
              <Typography variant="h5">Requirement Details</Typography>
            </Box>
            <Chip
              label={selectedRequirement?.status}
              color={getStatusColor(selectedRequirement?.status || '')}
              sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequirement && (
            <Box>
              {/* Title Section */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.main' }}>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {selectedRequirement.title}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                  Category: {getCategoryLabel(selectedRequirement.category)}
                </Typography>
              </Paper>

              {/* Buyer Information */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Buyer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Buyer Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRequirement.buyer_name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedRequirement.buyer_email}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Budget & Delivery */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Budget & Delivery
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.main' }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Total Budget
                      </Typography>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {selectedRequirement.currency} {Number(selectedRequirement.total_budget || 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Delivery From
                    </Typography>
                    <Typography variant="h6">
                      {format(new Date(selectedRequirement.delivery_date_from), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Delivery To
                    </Typography>
                    <Typography variant="h6">
                      {format(new Date(selectedRequirement.delivery_date_to), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Requirements Details */}
              {(selectedRequirement.description || selectedRequirement.quality_requirements || selectedRequirement.payment_terms) && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    Requirements Details
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedRequirement.description && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Description
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="body1">{selectedRequirement.description}</Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedRequirement.quality_requirements && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Quality Requirements
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="body1">{selectedRequirement.quality_requirements}</Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedRequirement.payment_terms && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Payment Terms
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="body1">{selectedRequirement.payment_terms}</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Specifications */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Specifications
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Certification Required
                    </Typography>
                    <Chip 
                      label={selectedRequirement.certification_required ? 'Yes' : 'No'} 
                      color={selectedRequirement.certification_required ? 'success' : 'default'}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Organic Required
                    </Typography>
                    <Chip 
                      label={selectedRequirement.organic_required ? 'Yes' : 'No'} 
                      color={selectedRequirement.organic_required ? 'success' : 'default'}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Timeline */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Timeline
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Created
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {format(new Date(selectedRequirement.created_at), 'EEEE, MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Expires
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="warning.main">
                      {format(new Date(selectedRequirement.expires_at), 'EEEE, MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailsOpen(false)} variant="contained" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AllBuyerRequirements
